import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  getBillsRecalculationActive,
  getBillsRecalculationCount,
  getBillsRecalculationCountByGuids,
  previewBillsRecalculation,
  previewBillsRecalculationByGuids,
  runBillsRecalculation,
  runBillsRecalculationByGuids,
} from "api";
import {
  BasicButton,
  COLORS,
  ConfirmActionModal,
  ConfirmButton,
  DangerButton,
  Dialog,
  Tabs,
  useDialogControls,
} from "components";
import { useDebouncedValue, useNotify } from "hooks";
import { extractErrorMessage } from "utils";
import { STATUS_CODES } from "consts";

import { GUID_COUNT_DEBOUNCE_MS, BUTTON_SX } from "./consts";
import { extractGuids } from "./helpers";
import { FilterModeTab, GuidModeTab, SuccessState } from "./components";
import { BillsRecalculationModalProps, RecalculationMode } from "./types";

export const BillsRecalculationModal = ({
  isOpen,
  onClose,
  displayFilters,
  queryParams,
}: BillsRecalculationModalProps) => {
  const notify = useNotify();
  const confirmRunDialog = useDialogControls();
  const [successState, setSuccessState] = useState<{
    isPreview: boolean;
  } | null>(null);
  const [mode, setMode] = useState<RecalculationMode>("filter");
  const [guidsText, setGuidsText] = useState("");

  const guidsPayload = useMemo(
    () => extractGuids(guidsText).join("\n"),
    [guidsText],
  );
  const hasValidGuids = guidsPayload.length > 0;
  const {
    debouncedValue: debouncedGuidsPayload,
    isPending: isGuidsCountPending,
  } = useDebouncedValue(guidsPayload, GUID_COUNT_DEBOUNCE_MS);

  const {
    data: isJobRunning = false,
    isLoading: isActiveLoading,
    refetch: refetchActive,
  } = useQuery({
    queryKey: ["billsRecalculationActive"],
    queryFn: getBillsRecalculationActive,
    enabled: isOpen,
    retry: false,
  });

  const {
    data: filterCountData,
    isLoading: isFilterCountLoading,
    isError: isFilterCountError,
    refetch: refetchFilterCount,
  } = useQuery({
    queryKey: ["billsRecalculationCount", queryParams],
    queryFn: () => getBillsRecalculationCount(queryParams),
    enabled: isOpen,
    retry: false,
  });

  const {
    data: guidCountData,
    isLoading: isGuidCountQueryLoading,
    isError: isGuidCountError,
  } = useQuery({
    queryKey: ["billsRecalculationCountByGuids", debouncedGuidsPayload],
    queryFn: () => getBillsRecalculationCountByGuids(debouncedGuidsPayload),
    enabled: isOpen && mode === "guid" && debouncedGuidsPayload.length > 0,
    retry: false,
  });

  const { mutateAsync: previewMutation, isLoading: isPreviewSubmitting } =
    useMutation(previewBillsRecalculation);
  const { mutateAsync: runMutation, isLoading: isRunSubmitting } = useMutation(
    runBillsRecalculation,
  );
  const {
    mutateAsync: previewGuidsMutation,
    isLoading: isPreviewGuidsSubmitting,
  } = useMutation(previewBillsRecalculationByGuids);
  const { mutateAsync: runGuidsMutation, isLoading: isRunGuidsSubmitting } =
    useMutation(runBillsRecalculationByGuids);

  useEffect(() => {
    if (!isOpen) return;
    setSuccessState(null);
    setMode("filter");
    setGuidsText("");
    refetchActive();
    refetchFilterCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isFilterCountError) {
      notify.error("Failed to load the eligible bill count. Please try again.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterCountError]);

  const filterEligibleCount = filterCountData?.count ?? null;
  const guidEligibleCount =
    debouncedGuidsPayload.length === 0 ? null : guidCountData?.count ?? null;
  const guidCountLoading =
    isGuidsCountPending ||
    (debouncedGuidsPayload.length > 0 && isGuidCountQueryLoading);
  const guidCountHasError =
    debouncedGuidsPayload.length > 0 &&
    !isGuidsCountPending &&
    isGuidCountError;

  useEffect(() => {
    if (guidCountHasError) {
      notify.error("Failed to load the eligible bill count. Please try again.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidCountHasError]);

  // Gate the whole body behind one loading state on open, instead of letting the tab bar,
  // count box, and alerts each pop in separately as their own queries resolve — that
  // piecemeal settling is what read as the modal "flickering" right after opening.
  const isInitialLoading = isActiveLoading || isFilterCountLoading;

  const eligibleCount =
    mode === "filter" ? filterEligibleCount : guidEligibleCount;
  const isSubmitting =
    isPreviewSubmitting ||
    isRunSubmitting ||
    isPreviewGuidsSubmitting ||
    isRunGuidsSubmitting;
  const canSubmit =
    !isJobRunning && !isActiveLoading && (mode === "filter" || hasValidGuids);

  const handleRun = async (preview: boolean) => {
    try {
      if (mode === "filter") {
        await (preview
          ? previewMutation(queryParams)
          : runMutation(queryParams));
      } else {
        await (preview
          ? previewGuidsMutation(guidsPayload)
          : runGuidsMutation(guidsPayload));
      }
      notify.success(
        preview
          ? "Preview recalculation started. You'll be notified by email when it's ready."
          : "Recalculation started. Track progress in the Jobs page.",
      );
      confirmRunDialog.close();
      setSuccessState({ isPreview: preview });
    } catch (error: any) {
      if (error?.response?.status === STATUS_CODES.CONFLICT) {
        notify.error(
          extractErrorMessage(error, "A recalculation is already in progress."),
        );
        confirmRunDialog.close();
        refetchActive();
        return;
      }
      notify.error(
        extractErrorMessage(
          error,
          "Failed to start recalculation. Please try again.",
        ),
      );
    }
  };

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title={<Box sx={{ color: COLORS.Mainblue }}>Recalculate Bills</Box>}
        dividers
        actions={
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            {successState ? (
              <BasicButton variant="contained" sx={BUTTON_SX} onClick={onClose}>
                Got it
              </BasicButton>
            ) : (
              <>
                <BasicButton
                  variant="outlined"
                  sx={BUTTON_SX}
                  onClick={onClose}
                >
                  {isJobRunning ? "Close" : "Cancel"}
                </BasicButton>
                <ConfirmButton
                  sx={BUTTON_SX}
                  disabled={!canSubmit || isSubmitting}
                  onClick={() => handleRun(true)}
                >
                  Run Preview
                </ConfirmButton>
                <DangerButton
                  sx={BUTTON_SX}
                  disabled={!canSubmit || isSubmitting}
                  onClick={confirmRunDialog.open}
                >
                  Run Recalculation
                </DangerButton>
              </>
            )}
          </Box>
        }
      >
        <Box sx={{ width: "100%", maxWidth: "540px" }}>
          {successState ? (
            <SuccessState isPreview={successState.isPreview} />
          ) : isInitialLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Tabs
              onChange={(index) => setMode(index === 0 ? "filter" : "guid")}
              tabs={[
                {
                  label: "Filter Mode",
                  content: (
                    <FilterModeTab
                      filters={displayFilters}
                      eligibleCount={filterEligibleCount}
                      isCountLoading={isFilterCountLoading}
                      isJobRunning={isJobRunning}
                    />
                  ),
                },
                {
                  label: "GUID Mode",
                  content: (
                    <GuidModeTab
                      guidsText={guidsText}
                      onGuidsTextChange={setGuidsText}
                      eligibleCount={guidEligibleCount}
                      isCountLoading={guidCountLoading}
                      isJobRunning={isJobRunning}
                    />
                  ),
                },
              ]}
            />
          )}
        </Box>
      </Dialog>

      <ConfirmActionModal
        isOpen={confirmRunDialog.isOpen}
        onCancel={confirmRunDialog.close}
        onConfirm={() => handleRun(false)}
        title="Confirm Recalculation"
        confirmButtonText="Run Recalculation"
        confirmButtonDisabled={isSubmitting}
      >
        You are about to recalculate emissions for{" "}
        <strong>
          {eligibleCount === null
            ? "all eligible"
            : eligibleCount.toLocaleString()}
        </strong>{" "}
        bill{eligibleCount === 1 ? "" : "s"}{" "}
        {mode === "filter"
          ? "matching the current filters."
          : "matching the submitted GUIDs."}
      </ConfirmActionModal>
    </>
  );
};
